import { useState, useCallback, useMemo } from 'react';
import { SlideConfig, FormData } from '../types';
import { SLIDES_CONFIG } from '../config/slidesConfig';

interface UseSliderNavigationReturn {
  currentIndex: number;
  currentSlide: SlideConfig | null;
  visibleSlides: SlideConfig[];
  totalProgressSteps: number;
  currentProgressStep: number;
  progress: number;
  goToSlide: (index: number) => void;
  goToSlideById: (id: string) => void;
  goNext: () => void;
  goPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
}

/**
 * Hook per gestire la navigazione dello slider
 * Filtra le slide in base alle condizioni e gestisce indici
 */
export function useSliderNavigation(formData: FormData): UseSliderNavigationReturn {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filtra le slide visibili in base alle condizioni
  const visibleSlides = useMemo(() => {
    return SLIDES_CONFIG.filter((slide) => {
      if (!slide.condition) return true;
      return slide.condition(formData);
    });
  }, [formData]);

  // Slide corrente
  const currentSlide = useMemo(() => {
    return visibleSlides[currentIndex] || null;
  }, [visibleSlides, currentIndex]);

  // Calcola gli step per il progresso (solo SINGLE_ANSWER e FORM_INPUT)
  const progressSlides = useMemo(() => {
    return visibleSlides.filter(
      (slide) => slide.type === 'SINGLE_ANSWER' || slide.type === 'FORM_INPUT'
    );
  }, [visibleSlides]);

  const totalProgressSteps = progressSlides.length;

  // Trova lo step corrente nella progress bar
  const currentProgressStep = useMemo(() => {
    if (!currentSlide) return 0;
    const index = progressSlides.findIndex((s) => s.id === currentSlide.id);
    return index >= 0 ? index : 0;
  }, [currentSlide, progressSlides]);

  // Calcola la percentuale di progresso
  const progress = useMemo(() => {
    if (totalProgressSteps <= 1) return 0;
    return (currentProgressStep / (totalProgressSteps - 1)) * 100;
  }, [currentProgressStep, totalProgressSteps]);

  // Trova l'indice della prossima slide visibile
  const getNextVisibleIndex = useCallback((): number => {
    if (currentIndex >= visibleSlides.length - 1) return -1;
    return currentIndex + 1;
  }, [currentIndex, visibleSlides.length]);

  // Trova l'indice della slide precedente visibile
  const getPrevVisibleIndex = useCallback((): number => {
    if (currentIndex <= 0) return -1;
    return currentIndex - 1;
  }, [currentIndex]);

  // Naviga a una slide specifica per indice
  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < visibleSlides.length) {
        setCurrentIndex(index);
      }
    },
    [visibleSlides.length]
  );

  // Naviga a una slide specifica per ID
  const goToSlideById = useCallback(
    (id: string) => {
      const index = visibleSlides.findIndex((slide) => slide.id === id);
      if (index >= 0) {
        setCurrentIndex(index);
      }
    },
    [visibleSlides]
  );

  // Vai alla prossima slide
  const goNext = useCallback(() => {
    const nextIndex = getNextVisibleIndex();
    if (nextIndex >= 0) {
      setCurrentIndex(nextIndex);
    }
  }, [getNextVisibleIndex]);

  // Vai alla slide precedente
  const goPrev = useCallback(() => {
    const prevIndex = getPrevVisibleIndex();
    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex);
    }
  }, [getPrevVisibleIndex]);

  // Controlli di navigazione
  const canGoNext = getNextVisibleIndex() >= 0;
  const canGoPrev = getPrevVisibleIndex() >= 0;

  return {
    currentIndex,
    currentSlide,
    visibleSlides,
    totalProgressSteps,
    currentProgressStep,
    progress,
    goToSlide,
    goToSlideById,
    goNext,
    goPrev,
    canGoNext,
    canGoPrev,
  };
}
